import { Control, Controller } from "react-hook-form";
import { useCallback, useMemo, useState } from "react";
import { NoxusInput } from "@/components/Form/InputField";
import { ConfigDictEntryWithType } from "@/types/api/spotflow/spotflow-config";
import {
  Plus,
  Trash,
  GripVertical,
  ChevronsUpDown,
  ChevronsDownUp,
  EllipsisVertical,
  ChevronUp,
  Copy,
  Info,
  ToggleRight,
  Braces,
  Text,
  Hash,
  List,
} from "lucide-react";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { NoxusIconButton } from "@/components/UI/NoxusIconButton";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { Button } from "@/components/shadcn/button";
import { SpotSelect } from "@/components/Form/SelectField";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { Badge } from "@/components/shadcn/badge";
import { cn } from "@/lib/utils";
import { Code } from "lucide-react";
import { CodeInput } from "@/components/Form/CodeField";

const NAME_TOOLTIP_MIN_LENGTH = 200;

// Must exclude `visibility`: transitioned, it stays `visible` for the whole
// duration and the actions linger over the badge after the pointer leaves.
const ROW_ACTION_TRANSITION = "transition-[color,background-color,box-shadow]";

const TOOLTIP_NO_HOVER_STEAL = "pointer-events-none";

const sanitizeFieldName = (
  value: string,
  allowDots: boolean = false,
  allowSpaces: boolean = false
) => {
  const allowed = `a-zA-Z0-9_${allowDots ? "." : ""}${allowSpaces ? " " : ""}`;
  let sanitized = value.replace(new RegExp(`[^${allowed}]`, "g"), "");
  if (allowSpaces) {
    sanitized = sanitized.trim();
  }
  if (sanitized.length > 0 && /^[0-9]/.test(sanitized)) {
    sanitized = "_" + sanitized;
  }
  return sanitized;
};
interface AccordionItemWithDragProps {
  id: string;
  value: string;
  isDisabled?: boolean;
  hasDuplicateName: boolean;
  displayName: string;
  typeValue: string;
  isOpen: boolean;
  entry: any;
  entryIndex: number;
  cantDelete: boolean;
  handleCopyClick: (entry: any) => void;
  handleDeleteClick: (index: number) => void;
  handleChangeEntry: (
    index: number,
    key: string,
    value: any,
    isNameField: boolean
  ) => void;
  props: IJsonSchemaBuilderConfigProps;
}

const AccordionItemWithDrag = ({
  id,
  value,
  isDisabled,
  hasDuplicateName,
  displayName,
  typeValue,
  isOpen,
  entry,
  entryIndex,
  cantDelete,
  handleCopyClick,
  handleDeleteClick,
  handleChangeEntry,
  props,
}: AccordionItemWithDragProps) => {
  const [localNameValue, setLocalNameValue] = useState<string | null>(null);

  const shownName =
    displayName || `${props.prefix_name || "Field"} ${entryIndex + 1}`;
  const showNameTooltip = shownName.length > NAME_TOOLTIP_MIN_LENGTH;

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id, disabled: isDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <AccordionItem
        value={value}
        className="border rounded-md px-4 last:border-b first:border-t bg-[#FCFCFC]"
      >
        {/* `-mx-4 px-4` is not a no-op: it spans the card's own padding so
            hover survives the gutter beside the actions. */}
        <div className="group/row -mx-4 px-4 flex items-center gap-2 [&>h3]:grow [&>h3]:min-w-0">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>
          <AccordionTrigger
            className="hover:no-underline py-3 grow items-center overflow-visible min-w-0"
            hideChevron
          >
            <div className="flex items-center justify-between w-full pr-2 min-w-0">
              <NoxusTooltip
                content={
                  showNameTooltip ? (
                    <span className="wrap-break-word">{shownName}</span>
                  ) : undefined
                }
                asChild
              >
                <span
                  className={cn(
                    "text-sm font-medium min-w-0 wrap-break-word",
                    showNameTooltip && "line-clamp-3"
                  )}
                >
                  {shownName}
                </span>
              </NoxusTooltip>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="grid items-center justify-items-end [&>*]:col-start-1 [&>*]:row-start-1">
                {typeValue && (
                  <div className="h-6 flex items-center group-hover/row:invisible">
                    <Badge variant="outline">{typeValue}</Badge>
                  </div>
                )}
                <div className="invisible group-hover/row:visible flex items-center gap-2 h-6">
                  <NoxusTooltip
                    content="Duplicate field"
                    classNameContent={TOOLTIP_NO_HOVER_STEAL}
                  >
                    <NoxusIconButton
                      icon={Copy}
                      variant="tertiary"
                      className={ROW_ACTION_TRANSITION}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyClick(entry);
                      }}
                      isDisabled={isDisabled}
                    />
                  </NoxusTooltip>
                  <NoxusTooltip
                    content={
                      cantDelete
                        ? "At least one field is required"
                        : "Delete field"
                    }
                    classNameContent={TOOLTIP_NO_HOVER_STEAL}
                  >
                    <NoxusIconButton
                      icon={Trash}
                      variant="tertiary"
                      className={ROW_ACTION_TRANSITION}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(entryIndex);
                      }}
                      isDisabled={cantDelete || isDisabled}
                    />
                  </NoxusTooltip>
                </div>
              </div>

              <Button variant="ghost" size="iconsm">
                <ChevronUp
                  className={cn(
                    "transition-transform duration-200",
                    !isOpen ? "rotate-180" : ""
                  )}
                />
              </Button>
            </div>
          </AccordionTrigger>
        </div>

        <AccordionContent className="pb-4 pt-2 px-0.25 border-t">
          <div className="grid grid-cols-12 gap-3">
            {props.keys.map((key, keyIndex) => {
              const isNameField = keyIndex === 0;
              const colSpan = key.col_span || 12;

              if (key.type_field && key.type_options) {
                const isTypeField = key.label === "Type";
                return (
                  <div
                    key={keyIndex}
                    className="flex flex-col gap-1.5"
                    style={{
                      gridColumn: `span ${colSpan}`,
                    }}
                  >
                    <label className="text-sm font-medium flex items-center gap-1">
                      {key.label}
                      {key.tooltip && (
                        <NoxusTooltip content={key.tooltip}>
                          <Info
                            size={16}
                            strokeWidth={1.25}
                            className="text-muted-foreground"
                          />
                        </NoxusTooltip>
                      )}
                    </label>
                    <SpotSelect
                      name={`temp_${entryIndex}_${keyIndex}`}
                      value={entry[key.label] || key.type_options[0]}
                      // SpotSelect emits the whole {value, label} option object
                      // — unwrap so the stored type is always a plain string.
                      onChange={(
                        selected: string | { value?: string } | null
                      ) =>
                        handleChangeEntry(
                          entryIndex,
                          key.label,
                          typeof selected === "string"
                            ? selected
                            : (selected?.value ?? key.type_options?.[0]),
                          false
                        )
                      }
                      options={key.type_options.map((opt: string) => ({
                        value: opt,
                        label: opt,
                      }))}
                      isDisabled={isDisabled}
                      customItemContent={
                        isTypeField
                          ? (option) => (
                              <div className="flex items-center gap-2">
                                {option?.label && getTypeIcon(option.label)}
                                <span>{option?.label}</span>
                              </div>
                            )
                          : undefined
                      }
                    />
                  </div>
                );
              } else {
                return (
                  <div
                    key={keyIndex}
                    className="flex flex-col gap-1.5"
                    style={{
                      gridColumn: `span ${colSpan}`,
                    }}
                  >
                    <label className="text-sm font-medium flex items-center gap-1">
                      {key.label}
                      {key.tooltip && (
                        <NoxusTooltip content={key.tooltip}>
                          <Info
                            size={16}
                            strokeWidth={1.25}
                            className="text-muted-foreground"
                          />
                        </NoxusTooltip>
                      )}
                    </label>
                    <NoxusInput
                      value={
                        isNameField && localNameValue !== null
                          ? localNameValue
                          : entry[key.label] || ""
                      }
                      customStyle="bg-background"
                      onChange={(e) => {
                        if (isNameField) {
                          setLocalNameValue(e.target.value);
                        } else {
                          handleChangeEntry(
                            entryIndex,
                            key.label,
                            e.target.value,
                            false
                          );
                        }
                      }}
                      onBlur={(e) => {
                        if (isNameField) {
                          handleChangeEntry(
                            entryIndex,
                            key.label,
                            e.target.value,
                            true
                          );
                          setLocalNameValue(null);
                        }
                      }}
                      placeholder={key.placeholder}
                      disable={isDisabled}
                      hasError={isNameField && hasDuplicateName}
                    />
                    {isNameField && hasDuplicateName && (
                      <span className="text-xs text-destructive">
                        Field names must be unique
                      </span>
                    )}
                  </div>
                );
              }
            })}
          </div>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
};

interface IJsonSchemaBuilderConfigProps {
  title?: string;
  subtitle?: string;
  value?: any;
  keys: ConfigDictEntryWithType[];
  tooltip?: string;
  onChange: (value: any[]) => void;
  prefix_name?: string;
  isDisabled?: boolean;
  allow_dots_in_name?: boolean;
  allow_spaces_in_name?: boolean;
  min_items?: number;
}

const getTypeIcon = (optionLabel: string) => {
  const label = optionLabel.toLowerCase();
  if (label.includes("text")) {
    return <Text className="w-4 h-4" />;
  }
  if (label.includes("boolean")) {
    return <ToggleRight className="w-4 h-4" />;
  }
  if (label.includes("number") || label.includes("integer")) {
    return <Hash className="w-4 h-4" />;
  }
  if (label.includes("json") || label.includes("object")) {
    return <Braces className="w-4 h-4" />;
  }
  if (label.includes("list")) {
    return <List className="w-4 h-4" />;
  }
  return null;
};

interface IJsonModeEditorProps {
  value: any[];
  keys: ConfigDictEntryWithType[];
  onCancel: () => void;
  onSubmit: (value: any[]) => void;
  isDisabled?: boolean;
  allow_dots_in_name?: boolean;
  allow_spaces_in_name?: boolean;
}

const JsonModeEditor = ({
  value,
  keys,
  onCancel,
  onSubmit,
  isDisabled,
  allow_dots_in_name,
  allow_spaces_in_name,
}: IJsonModeEditorProps) => {
  const normalizeValue = useCallback((data: any[]) => {
    return data.map((entry) => {
      const normalized = { ...entry };
      Object.keys(normalized).forEach((key) => {
        const val = normalized[key];
        if (typeof val === "object" && val?.value) {
          normalized[key] = val.value;
        }
      });
      return normalized;
    });
  }, []);

  const [jsonValue, setJsonValue] = useState(() =>
    JSON.stringify(normalizeValue(value), null, 2)
  );
  const [errors, setErrors] = useState<string[]>([]);

  const validateAndSubmit = useCallback(() => {
    const validationErrors: string[] = [];

    let parsed: any;
    try {
      parsed = JSON.parse(jsonValue);
    } catch (e) {
      setErrors([`Invalid JSON: ${(e as Error).message}`]);
      return;
    }

    if (!Array.isArray(parsed)) {
      setErrors(["Value must be an array of objects"]);
      return;
    }

    if (keys.length === 0) {
      setErrors(["No keys defined for validation"]);
      return;
    }

    const nameKey = keys[0].label;
    const seenNames = new Set<string>();

    parsed.forEach((entry, index) => {
      if (typeof entry !== "object" || entry === null) {
        validationErrors.push(
          `Field ${index + 1}: Must be an object, got ${typeof entry}`
        );
        return;
      }

      const entryKeys = Object.keys(entry);
      const allowedKeys = keys.map((k) => k.label);
      const fieldName = entry[nameKey] || `Field ${index + 1}`;
      const fieldLabel =
        entry[nameKey] !== undefined
          ? `"${fieldName}" (${index + 1})`
          : `Field ${index + 1}`;

      entryKeys.forEach((key) => {
        if (!allowedKeys.includes(key)) {
          validationErrors.push(
            `${fieldLabel}: Unknown key "${key}". Allowed keys: ${allowedKeys.join(", ")}`
          );
        }
      });

      keys.forEach((key) => {
        if (!(key.label in entry)) {
          validationErrors.push(
            `${fieldLabel}: Missing required key "${key.label}"`
          );
          return;
        }

        if (key.type_field && key.type_options) {
          const rawValue = entry[key.label];
          const actualValue =
            typeof rawValue === "object" && rawValue?.value
              ? rawValue.value
              : rawValue;

          if (!key.type_options.includes(actualValue)) {
            validationErrors.push(
              `${fieldLabel}: Invalid value "${actualValue}" for "${key.label}". Must be one of: ${key.type_options.join(", ")}`
            );
          }
        }
      });

      const rawName = entry[nameKey];
      if (rawName !== undefined) {
        const sanitizedName = sanitizeFieldName(
          String(rawName),
          allow_dots_in_name,
          allow_spaces_in_name
        );
        entry[nameKey] = sanitizedName;

        const lowerName = sanitizedName.toLowerCase();
        if (lowerName) {
          if (seenNames.has(lowerName)) {
            validationErrors.push(
              `Field ${index + 1}: Duplicate name "${sanitizedName}"`
            );
          } else {
            seenNames.add(lowerName);
          }
        }
      }
    });

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    onSubmit(parsed);
  }, [jsonValue, keys, onSubmit, allow_dots_in_name, allow_spaces_in_name]);

  return (
    <div className="flex flex-col gap-3">
      <CodeInput
        value={jsonValue}
        onChange={(value: string) => setJsonValue(value)}
        isDisabled={isDisabled}
        placeholder="Enter JSON array..."
      />

      {errors.length > 0 && (
        <div className="bg-destructive/10 border border-destructive rounded-md p-3">
          <p className="font-medium text-sm mb-2 text-destructive">
            Validation Errors:
          </p>
          <ul className="list-disc pl-5 text-sm space-y-1">
            {errors.map((error, i) => (
              <li key={i} className="text-destructive">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel} disabled={isDisabled}>
          Cancel
        </Button>
        <Button onClick={validateAndSubmit} disabled={isDisabled}>
          Apply Changes
        </Button>
      </div>
    </div>
  );
};

const JsonSchemaBuilderConfigComponent = (
  props: IJsonSchemaBuilderConfigProps
) => {
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [isJsonMode, setIsJsonMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const cantDelete = useMemo(() => {
    const min = props.min_items ?? 1;
    return props.value.length <= min;
  }, [props.value, props.min_items]);

  const { isDisabled, allow_dots_in_name, allow_spaces_in_name } = props;

  const handleExpandAll = useCallback(() => {
    setOpenItems(props.value.map((_: any, index: number) => index.toString()));
  }, [props.value]);

  const handleCollapseAll = useCallback(() => {
    setOpenItems([]);
  }, []);

  const handleDragStart = useCallback(() => {
    setOpenItems([]);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || isDisabled) return;

      if (active.id !== over.id) {
        const oldIndex = Number(active.id);
        const newIndex = Number(over.id);
        const newValue = arrayMove(props.value, oldIndex, newIndex);
        props.onChange(newValue);
      }
    },
    [props, isDisabled]
  );

  const duplicateNameIndices = useMemo(() => {
    if (!props.keys.length) return new Set<number>();

    const nameKey = props.keys[0].label;
    const names = props.value.map(
      (entry: any) => entry[nameKey]?.toLowerCase() || ""
    );
    const duplicates = new Set<number>();
    const seen = new Map<string, number>();

    for (let i = 0; i < names.length; i++) {
      if (!names[i]) continue;
      if (seen.has(names[i])) {
        duplicates.add(i);
      } else {
        seen.set(names[i], i);
      }
    }

    return duplicates;
  }, [props.value, props.keys]);

  const ensureUniqueName = useCallback(
    (newName: string, currentIndex: number) => {
      if (!newName || !props.keys.length) return newName;

      const nameKey = props.keys[0].label;
      const existingNames = props.value
        .map((entry: any, idx: number) => ({
          name: entry[nameKey]?.toLowerCase() || "",
          index: idx,
        }))
        .filter((item: any) => item.index !== currentIndex && item.name);

      const lowerNewName = newName.toLowerCase();
      const isDuplicate = existingNames.some(
        (item: any) => item.name === lowerNewName
      );

      if (!isDuplicate) {
        return newName;
      }

      let counter = 2;
      let uniqueName = `${newName}_${counter}`;
      while (
        existingNames.some(
          (item: any) => item.name === uniqueName.toLowerCase()
        )
      ) {
        counter++;
        uniqueName = `${newName}_${counter}`;
      }

      return uniqueName;
    },
    [props.value, props.keys]
  );

  const handleAddClick = useCallback(() => {
    if (isDisabled) return;
    const controlling_entry = props.keys[0];

    const prefix = props.prefix_name || "Field";
    const capitalizedPrefix = prefix.charAt(0).toUpperCase() + prefix.slice(1);

    // Extract numbers from existing field names to find the max
    const existingNumbers = props.value
      .map((item: any) => {
        const name = item[controlling_entry.label] || "";
        // Match both "Field 1" and "field_1" patterns
        const match = name.match(
          new RegExp(
            `^(?:${capitalizedPrefix} |${prefix.toLowerCase()}_)(\\d+)$`
          )
        );
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((n: number) => !isNaN(n));

    const maxValue =
      existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;

    const new_value: Record<string, string> = {};
    props.keys.forEach((key, index) => {
      if (index === 0) {
        new_value[key.label] = `${capitalizedPrefix} ${maxValue + 1}`;
      } else if (key.type_field && key.type_options) {
        new_value[key.label] = key.type_options[0];
      } else {
        new_value[key.label] = "";
      }
    });
    const newDictArray = [...props.value, new_value];
    props.onChange(newDictArray);
  }, [props, isDisabled]);

  const handleDeleteClick = useCallback(
    (indexToRemove: number) => {
      if (isDisabled) return;
      if (cantDelete) return;

      const newDictArray = props.value.filter(
        (_: any, index: number) => index !== indexToRemove
      );
      props.onChange(newDictArray);
    },
    [props, cantDelete, isDisabled]
  );

  const handleChangeEntry = useCallback(
    (
      indexToUpdate: number,
      key: string,
      newValue: any,
      isNameField: boolean = false
    ) => {
      let finalValue = newValue;
      if (isNameField) {
        finalValue = sanitizeFieldName(
          newValue,
          allow_dots_in_name,
          allow_spaces_in_name
        );
        finalValue = ensureUniqueName(finalValue, indexToUpdate);
      }
      const newDictArray = props.value.map((entry: any, index: number) => {
        if (index === indexToUpdate) {
          return { ...entry, [key]: finalValue };
        }
        return entry;
      });
      props.onChange(newDictArray);
    },
    [props, ensureUniqueName, allow_dots_in_name, allow_spaces_in_name]
  );

  const getEntryDisplayValue = useCallback(
    (entry: any) => {
      if (props.keys.length === 0) return "";
      const firstKey = props.keys[0].label;
      return entry[firstKey] || "";
    },
    [props.keys]
  );

  const getEntryTypeValue = useCallback(
    (entry: any) => {
      const typeKey = props.keys.find((key) => key.type_field);
      if (!typeKey) return "";
      const result = entry[typeKey.label];
      if (typeof result === "object" && result?.value) {
        return result.value || "";
      }
      return result || "";
    },
    [props.keys]
  );

  const handleCopyClick = useCallback(
    (entry: any) => {
      if (isDisabled) return;

      const nameKey = props.keys[0].label;
      const originalName = entry[nameKey] || "";

      const newName = ensureUniqueName(originalName, -1);

      const newEntry = { ...entry, [nameKey]: newName };
      const newDictArray = [...props.value, newEntry];
      props.onChange(newDictArray);
    },
    [props, isDisabled, ensureUniqueName]
  );

  const handleEnterJsonMode = useCallback(() => {
    if (isDisabled) return;
    setIsJsonMode(true);
  }, [isDisabled]);

  const handleExitJsonMode = useCallback(() => {
    setIsJsonMode(false);
  }, []);

  const handleJsonSubmit = useCallback(
    (value: any[]) => {
      props.onChange(value);
      setIsJsonMode(false);
    },
    [props]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between">
        <div>
          {props.title && (
            <NoxusFormLabel label={props.title} tooltip={props.tooltip} />
          )}
          {props.subtitle && (
            <p className="text-xs text-muted-foreground">{props.subtitle}</p>
          )}
        </div>
        {!isJsonMode && props.value.length > 0 && (
          <div className="flex gap-2 mb-2">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger>
                <Button variant="ghost" size="icon" disabled={isDisabled}>
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExpandAll();
                  }}
                >
                  <ChevronsUpDown />
                  Expand All
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCollapseAll();
                  }}
                >
                  <ChevronsDownUp />
                  Collapse All
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isJsonMode) {
                      handleExitJsonMode();
                    } else {
                      handleEnterJsonMode();
                    }
                  }}
                >
                  <Code className="w-4 h-4" />
                  JSON Mode
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {isJsonMode ? (
        <JsonModeEditor
          value={props.value}
          keys={props.keys}
          onCancel={handleExitJsonMode}
          onSubmit={handleJsonSubmit}
          isDisabled={isDisabled}
          allow_dots_in_name={allow_dots_in_name}
          allow_spaces_in_name={allow_spaces_in_name}
        />
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={props.value.map((_: any, idx: number) => idx.toString())}
              strategy={verticalListSortingStrategy}
            >
              <Accordion
                type="multiple"
                value={openItems}
                onValueChange={setOpenItems}
                className="flex flex-col gap-3"
              >
                {props.value?.map((entry: any, entryIndex: number) => {
                  const hasDuplicateName = duplicateNameIndices.has(entryIndex);
                  const displayName = getEntryDisplayValue(entry);
                  const typeValue = getEntryTypeValue(entry);
                  const isOpen = openItems.includes(entryIndex.toString());

                  return (
                    <AccordionItemWithDrag
                      key={entryIndex}
                      id={entryIndex.toString()}
                      value={entryIndex.toString()}
                      isDisabled={isDisabled}
                      hasDuplicateName={hasDuplicateName}
                      displayName={displayName}
                      typeValue={typeValue}
                      isOpen={isOpen}
                      entry={entry}
                      entryIndex={entryIndex}
                      cantDelete={cantDelete}
                      handleCopyClick={handleCopyClick}
                      handleDeleteClick={handleDeleteClick}
                      handleChangeEntry={handleChangeEntry}
                      props={props}
                    />
                  );
                })}
              </Accordion>
            </SortableContext>
          </DndContext>

          <Button
            onClick={handleAddClick}
            variant="outline"
            className="w-full mt-2"
            disabled={isDisabled}
          >
            <Plus className="w-4 h-4" />
            Add {props.prefix_name || "Field"}
          </Button>
        </>
      )}
    </div>
  );
};

interface IJsonSchemaBuilderConfig {
  name: string;
  label?: string;
  subtitle?: string;
  control: Control<any>;
  defaultValue?: any;
  tooltip?: string;
  keys: ConfigDictEntryWithType[];
  prefix_name?: string;
  isDisabled?: boolean;
  allow_dots_in_name?: boolean;
  allow_spaces_in_name?: boolean;
  min_items?: number;
}

export const JsonSchemaBuilderConfig = ({
  name,
  label,
  subtitle,
  control,
  defaultValue,
  tooltip,
  keys,
  prefix_name,
  isDisabled,
  allow_dots_in_name,
  allow_spaces_in_name,
  min_items,
}: IJsonSchemaBuilderConfig) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => {
        // Migrate old ConfigDictSelectArray format {label, value} → {Name, Type}
        const rawValue: any[] = field.value || [];
        const nameKey = keys[0]?.label;
        const typeKey = keys.find((k) => k.type_field)?.label;
        if (
          nameKey &&
          typeKey &&
          rawValue.length > 0 &&
          rawValue[0]?.label &&
          !rawValue[0]?.[nameKey]
        ) {
          const migrated = rawValue.map((v: any) => ({
            [nameKey]: v.label || v.Name || "",
            [typeKey]: v.value || v.Type || v[typeKey] || "Text",
          }));
          field.onChange(migrated);
        }

        return (
          <JsonSchemaBuilderConfigComponent
            title={label}
            subtitle={subtitle}
            value={field.value || []}
            keys={keys}
            tooltip={tooltip}
            onChange={field.onChange}
            prefix_name={prefix_name}
            isDisabled={isDisabled}
            allow_dots_in_name={allow_dots_in_name}
            allow_spaces_in_name={allow_spaces_in_name}
            min_items={min_items}
          />
        );
      }}
    />
  );
};
